import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Building2, CalendarClock, DollarSign, PackageOpen, PhoneCall, Trophy, Users } from "lucide-react";
import PageHeader, { card } from "@/app/PageHeader";
import { useSession } from "@/app/session";

const PIPELINE = [["new", "New"], ["contacted", "Contacted"], ["meeting", "Meeting"], ["proposal", "Proposal"], ["won", "Won"]];
const fmtDate = (iso) => (iso ? new Date(`${iso}T12:00:00`).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "");
const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

const Stat = ({ icon: Icon, label, value, to, tone = "text-white" }) => (
  <Link to={to} className={`${card} group flex flex-col gap-3 p-5 transition-colors hover:border-vortex-cyan/40`}>
    <span className="flex items-center justify-between font-mono text-[10px] tracking-[0.2em] text-slate-500 uppercase">
      {label} <Icon size={15} className="text-vortex-cyan" />
    </span>
    <span className={`font-heading text-3xl font-bold ${tone}`}>{value ?? "—"}</span>
  </Link>
);

const Panel = ({ title, to, linkLabel, children, testId }) => (
  <section className={card} data-testid={testId}>
    <header className="flex items-center justify-between border-b border-white/10 px-5 py-4">
      <h3 className="font-heading text-base font-bold text-white">{title}</h3>
      {to && (
        <Link to={to} className="flex items-center gap-1 text-xs text-vortex-cyan hover:underline">
          {linkLabel} <ArrowRight size={12} />
        </Link>
      )}
    </header>
    <div className="divide-y divide-white/5">{children}</div>
  </section>
);

const Empty = ({ children }) => <p className="px-5 py-6 text-sm text-slate-500">{children}</p>;

const Row = ({ to, title, detail, right, rightTone = "text-slate-500" }) => (
  <Link to={to} className="flex items-start gap-4 px-5 py-3.5 transition-colors hover:bg-white/5">
    <span className="min-w-0 flex-1">
      <span className="block truncate text-sm text-slate-100">{title}</span>
      {detail && <span className="mt-0.5 block truncate text-xs text-slate-500">{detail}</span>}
    </span>
    {right && <span className={`shrink-0 text-xs ${rightTone}`}>{right}</span>}
  </Link>
);

// The first screen after sign-in: everything that needs doing today across sales, service and stock.
const Home = () => {
  const { api } = useSession();
  const [crm, setCrm] = useState(null);
  const [service, setService] = useState(null);
  const [stock, setStock] = useState(null);
  const [feedback, setFeedback] = useState(null);

  useEffect(() => {
    // Each panel loads on its own so one slow or failing section doesn't blank the page.
    api.get("/crm/summary").then((r) => setCrm(r.data)).catch(() => setCrm(false));
    api.get("/service/overview").then((r) => setService(r.data)).catch(() => setService(false));
    api.get("/planner/inventory/overview").then((r) => setStock(r.data)).catch(() => setStock(false));
    api.get("/feedback").then((r) => setFeedback(r.data.slice(0, 5))).catch(() => setFeedback(false));
  }, [api]);

  const openDeals = crm ? ["contacted", "meeting", "proposal"].reduce((n, s) => n + (crm.by_status?.[s] || 0), 0) : null;
  const lowMachines = stock ? new Set(stock.rows.filter((r) => r.low || r.current === 0).map((r) => r.machine_id)) : null;
  const restock = stock
    ? Object.values(stock.rows.filter((r) => r.deficit > 0).reduce((acc, r) => {
      const m = acc[r.machine_id] || { id: r.machine_id, name: r.machine_name, units: 0, empty: 0 };
      m.units += r.deficit;
      if (r.current === 0) m.empty += 1;
      acc[r.machine_id] = m;
      return acc;
    }, {})).sort((a, b) => b.units - a.units).slice(0, 6)
    : [];
  const greeting = (() => {
    const h = new Date().getHours();
    return h < 12 ? "Good morning" : h < 18 ? "Good afternoon" : "Good evening";
  })();

  return (
    <div data-testid="admin-home">
      <PageHeader title={greeting}
        intro={`Here's what needs you today, ${new Date().toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}.`} />

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Stat icon={PhoneCall} label="Follow-ups today" value={crm ? crm.today.length : null} to="/admin/crm" tone="text-vortex-cyan" />
        <Stat icon={Users} label="Open deals" value={openDeals} to="/admin/pipeline" />
        <Stat icon={CalendarClock} label="Visits due" value={service ? service.due.length : null} to="/admin/service"
          tone={service && service.due.length ? "text-amber-300" : "text-white"} />
        <Stat icon={PackageOpen} label="Machines low on stock" value={lowMachines ? lowMachines.size : null} to="/admin/planner/fill-planner"
          tone={lowMachines && lowMachines.size ? "text-amber-300" : "text-white"} />
      </div>

      {crm && (
        <Link to="/admin/pipeline" className={`${card} mt-4 grid grid-cols-5 divide-x divide-white/10 transition-colors hover:border-vortex-cyan/40`} data-testid="home-pipeline">
          {PIPELINE.map(([k, label]) => (
            <span key={k} className="px-3 py-4 text-center">
              <span className="block font-heading text-xl font-bold text-white">{crm.by_status?.[k] || 0}</span>
              <span className="mt-1 flex items-center justify-center gap-1 font-mono text-[9px] tracking-[0.15em] text-slate-500 uppercase sm:text-[10px]">
                {k === "won" && <Trophy size={11} className="text-vortex-cyan" />}{label}
              </span>
            </span>
          ))}
        </Link>
      )}

      <div className="mt-6 grid grid-cols-1 gap-6 xl:grid-cols-2">
        <Panel title="Sales follow-ups" to="/admin/crm" linkLabel="All leads" testId="home-followups">
          {crm === null ? <Empty>Loading...</Empty>
            : crm === false ? <Empty>Could not load leads.</Empty>
            : crm.today.length === 0 ? <Empty>Nothing urgent. Find new businesses to pitch from Leads.</Empty>
            : crm.today.slice(0, 8).map((t) => (
              <Row key={t.id} to={`/admin/crm?lead=${t.id}`} title={t.company} detail={t.step.title}
                right={`Priority ${t.priority}`} rightTone="font-mono uppercase tracking-[0.15em] text-[10px] text-vortex-cyan" />
            ))}
        </Panel>

        <Panel title="Service visits due" to="/admin/service" linkLabel="Accounts" testId="home-visits">
          {service === null ? <Empty>Loading...</Empty>
            : service === false ? <Empty>Could not load service.</Empty>
            : service.due.length + service.upcoming.length === 0 ? <Empty>No machines due this week.</Empty>
            : [...service.due, ...service.upcoming].slice(0, 8).map((m) => (
              <Row key={m.id} to={`/admin/service?account=${m.account_id}`} title={`${m.account_name}: ${m.name}`}
                detail={m.address}
                right={m.days_overdue ? `${m.days_overdue}d overdue` : m.never_serviced ? "Never serviced" : `Due ${fmtDate(m.next_due)}`}
                rightTone={m.days_overdue || m.never_serviced ? "text-amber-300" : "text-slate-500"} />
            ))}
        </Panel>

        <Panel title="Restock needs" to="/admin/planner/fill-planner" linkLabel="Fill planner" testId="home-restock">
          {stock === null ? <Empty>Loading...</Empty>
            : stock === false ? <Empty>Could not load stock levels.</Empty>
            : restock.length === 0 ? <Empty>Every machine is at par.</Empty>
            : restock.map((m) => (
              <Row key={m.id} to={`/admin/planner/machines/${m.id}`} title={m.name}
                detail={m.empty ? `${m.empty} empty slot${m.empty === 1 ? "" : "s"}` : "Below par"}
                right={`${m.units} units short`} rightTone={m.empty ? "text-amber-300" : "text-slate-500"} />
            ))}
        </Panel>

        <Panel title="Contracts to renew" to="/admin/service" linkLabel="Accounts" testId="home-renewals">
          {service === null ? <Empty>Loading...</Empty>
            : service === false ? <Empty>Could not load contracts.</Empty>
            : service.renewals.length === 0 ? <Empty>No contracts end or renew in the next while.</Empty>
            : service.renewals.slice(0, 6).map((c) => (
              <Row key={c.id} to={`/admin/service?account=${c.account_id}`} title={c.account_name}
                detail={`${c.title || "Agreement"} · notice by ${fmtDate(c.notice_by)}`}
                right={`${c.state === "renewing" ? "Renews" : "Ends"} ${fmtDate(c.term_end)}`}
                rightTone={c.state === "expiring" ? "text-red-300" : "text-amber-300"} />
            ))}
        </Panel>

        <Panel title="Latest feedback" to="/admin/feedback" linkLabel="Inbox" testId="home-feedback">
          {feedback === null ? <Empty>Loading...</Empty>
            : feedback === false ? <Empty>Could not load feedback.</Empty>
            : feedback.length === 0 ? <Empty>No feedback yet.</Empty>
            : feedback.map((f) => (
              <Row key={f.id} to="/admin/feedback" title={f.message} detail={`${f.location_name} · ${f.category}`}
                right={new Date(f.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })} />
            ))}
        </Panel>

        {service && (
          <Panel title="This month" to="/admin/service" linkLabel="Visit log" testId="home-month">
            <div className="grid grid-cols-2 gap-px bg-white/5 sm:grid-cols-4">
              {[
                [Building2, "Active accounts", service.stats.active_accounts],
                [CalendarClock, "Visits", service.stats.visits_this_month],
                [PackageOpen, "Units stocked", service.stats.units_this_month],
                [DollarSign, "Cash collected", money(service.stats.cash_this_month)],
              ].map(([Icon, label, value]) => (
                <div key={label} className="bg-vortex-surface px-5 py-4">
                  <span className="flex items-center gap-1.5 font-mono text-[9px] tracking-[0.15em] text-slate-500 uppercase"><Icon size={11} />{label}</span>
                  <span className="mt-2 block font-heading text-xl font-bold text-white">{value}</span>
                </div>
              ))}
            </div>
          </Panel>
        )}
      </div>
    </div>
  );
};

export default Home;
